/**
 * MCP Tools for BrandBook
 * 
 * These tools allow AI Agents to access and manage BrandBook data.
 * BrandBook contains detailed brand guidelines derived from website analysis,
 * including voice analysis, visual identity, and messaging guidelines.
 * 
 * Used by: CreativeAgent, AdsStrategistAgent, ResearchAgent
 */

import { MCPTool } from '../types';
import { brandBookService } from '../../services/brandBookService';
import { getMCPServer } from '../server/MCPServer';

// ============================================================================
// GET BRAND BOOK
// ============================================================================

export const getBrandBookTool: MCPTool = {
  name: 'get_brand_book',
  description: `Retrieves BrandBook data for a domain.
BrandBook contains detailed brand guidelines including:
- Voice analysis (tone, address form, formality)
- Visual identity (colors, logo, image style)
- Key messages and USPs
- Do's and Don'ts for copy
- Image generation prompts

Use this for consistent brand representation in content creation.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      domain: {
        type: 'string',
        description: 'Domain to look up (e.g., "example.com")'
      },
      brandBookId: {
        type: 'string',
        description: 'Or provide the BrandBook ID directly'
      }
    },
    required: []
  },

  async execute(params, context) {
    try {
      if (!params.domain && !params.brandBookId) {
        return {
          success: false,
          error: 'Either domain or brandBookId must be provided'
        };
      }

      let brandBook;
      
      if (params.brandBookId) {
        brandBook = await brandBookService.getBrandBook(context.orgId, params.brandBookId);
      } else {
        brandBook = await brandBookService.getBrandBookByDomain(context.orgId, params.domain);
      }

      if (!brandBook) {
        return {
          success: false,
          found: false,
          message: `No BrandBook found for ${params.domain || params.brandBookId}. Use 'create_brand_book' to generate one.`
        };
      }

      // Return structured data for AI
      return {
        success: true,
        found: true,
        brandBook: {
          id: brandBook.id,
          url: brandBook.url,
          domain: brandBook.domain,
          
          // Voice & Tone
          voice: {
            addressForm: brandBook.voice?.addressForm || 'Sie',
            formality: brandBook.voice?.formality || 'formal',
            confidence: brandBook.voice?.confidence || 0,
            toneAttributes: brandBook.voice?.toneAttributes || [],
            writingStyle: brandBook.voice?.writingStyle,
            keyMessages: brandBook.voice?.keyMessages || [],
            doStatements: brandBook.voice?.doStatements || [],
            dontStatements: brandBook.voice?.dontStatements || []
          },
          
          // Visual Identity
          visual: {
            colors: brandBook.colors || [],
            logoUrl: brandBook.logoUrl,
            visualStyle: brandBook.visualStyle,
            imageStyle: brandBook.imageStyle
          },
          
          // Brand Info
          businessName: brandBook.businessName,
          industry: brandBook.industry,
          usp: brandBook.usp,
          tagline: brandBook.tagline,
          offerings: brandBook.offerings || [],
          targetAudience: brandBook.targetAudience,
          
          // Guidelines (if generated)
          guidelines: brandBook.guidelines ? {
            messageMap: brandBook.guidelines.messageMap,
            copyGuidelines: brandBook.guidelines.copyGuidelines,
            promptLibrary: brandBook.guidelines.promptLibrary?.length || 0
          } : null,
          
          // Metadata
          lastUpdated: brandBook.updatedAt
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to get BrandBook: ${error.message}`
      };
    }
  }
};

// ============================================================================
// CREATE BRAND BOOK
// ============================================================================

export const createBrandBookTool: MCPTool = {
  name: 'create_brand_book',
  description: `Generates a new BrandBook from a website URL.
Performs deep analysis including:
- Website scraping and content extraction
- AI-powered voice/tone analysis
- Visual identity extraction (colors, logo)
- Industry detection
- Key message identification

Use this to create brand guidelines for consistent content.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      url: {
        type: 'string',
        description: 'Website URL to analyze (e.g., "https://example.com")'
      }
    },
    required: ['url']
  },

  async execute(params, context) {
    try {
      // Check if BrandBook already exists
      const domain = params.url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];
      const existing = await brandBookService.getBrandBookByDomain(context.orgId, domain);
      
      if (existing) {
        return {
          success: true,
          alreadyExists: true,
          message: `BrandBook already exists for this domain. Use 'get_brand_book' to retrieve it.`,
          brandBookId: existing.id,
          businessName: existing.businessName
        };
      }

      // Generate new BrandBook
      const brandBook = await brandBookService.generateBrandBook(
        params.url,
        context.userId
      );

      // Save to org
      await brandBookService.saveBrandBook(context.orgId, brandBook);

      return {
        success: true,
        created: true,
        brandBook: {
          id: brandBook.id,
          domain: brandBook.domain,
          businessName: brandBook.businessName,
          industry: brandBook.industry,
          voiceAnalyzed: !!brandBook.voice,
          colorsExtracted: brandBook.colors?.length || 0
        },
        message: `BrandBook created for "${brandBook.businessName}"`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to create BrandBook: ${error.message}`
      };
    }
  }
};

// ============================================================================
// LIST BRAND BOOKS
// ============================================================================

export const listBrandBooksTool: MCPTool = {
  name: 'list_brand_books',
  description: `Lists all BrandBooks for the organization.
Returns a summary of each including business name, domain, and key info.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      limit: {
        type: 'number',
        description: 'Maximum number of records to return (default: 20)'
      }
    },
    required: []
  },

  async execute(params, context) {
    try {
      const brandBooks = await brandBookService.getBrandBooks(context.orgId);
      
      const limited = brandBooks.slice(0, params.limit || 20);

      return {
        success: true,
        count: limited.length,
        brandBooks: limited.map(bb => ({
          id: bb.id,
          domain: bb.domain,
          businessName: bb.businessName,
          industry: bb.industry,
          hasVoice: bb.hasVoice,
          hasGuidelines: bb.hasGuidelines,
          createdAt: bb.createdAt
        }))
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to list BrandBooks: ${error.message}`
      };
    }
  }
};

// ============================================================================
// GET IMAGE CONTEXT FROM BRAND BOOK
// ============================================================================

export const getImageContextTool: MCPTool = {
  name: 'get_image_context',
  description: `Extracts image generation context from a BrandBook.
Returns brand-consistent settings for image generation including:
- Color palette
- Visual style
- Image style preferences
- Subject recommendations

Use this before generating images to ensure brand consistency.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      domain: {
        type: 'string',
        description: 'Domain to get image context for'
      },
      brandBookId: {
        type: 'string',
        description: 'Or provide BrandBook ID directly'
      }
    },
    required: []
  },

  async execute(params, context) {
    try {
      if (!params.domain && !params.brandBookId) {
        return {
          success: false,
          error: 'Either domain or brandBookId must be provided'
        };
      }

      let brandBook;
      if (params.brandBookId) {
        brandBook = await brandBookService.getBrandBook(context.orgId, params.brandBookId);
      } else {
        brandBook = await brandBookService.getBrandBookByDomain(context.orgId, params.domain);
      }

      if (!brandBook) {
        return {
          success: false,
          found: false,
          message: 'No BrandBook found. Create one first.'
        };
      }

      // Extract image context
      const imageContext = brandBookService.getImageGenContext(brandBook);

      return {
        success: true,
        imageContext: {
          businessName: imageContext.businessName,
          industry: imageContext.industry,
          colors: imageContext.colors,
          visualStyle: imageContext.visualStyle,
          imageStyle: imageContext.imageStyle,
          targetAudience: imageContext.targetAudience,
          brandTone: imageContext.brandTone,
          promptLibrary: brandBook.guidelines?.promptLibrary?.slice(0, 5) || []
        },
        readyForImageGen: true
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to get image context: ${error.message}`
      };
    }
  }
};

// ============================================================================
// GET AD COPY CONTEXT FROM BRAND BOOK
// ============================================================================

export const getAdCopyContextTool: MCPTool = {
  name: 'get_ad_copy_context',
  description: `Extracts ad copy context from a BrandBook.
Returns guidelines for writing brand-consistent ad copy including:
- Voice/tone settings
- Address form (Sie/Du)
- Key messages to incorporate
- Do's and Don'ts

Use this before generating ad copy.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      domain: {
        type: 'string',
        description: 'Domain to get ad copy context for'
      },
      brandBookId: {
        type: 'string',
        description: 'Or provide BrandBook ID directly'
      }
    },
    required: []
  },

  async execute(params, context) {
    try {
      if (!params.domain && !params.brandBookId) {
        return {
          success: false,
          error: 'Either domain or brandBookId must be provided'
        };
      }

      let brandBook;
      if (params.brandBookId) {
        brandBook = await brandBookService.getBrandBook(context.orgId, params.brandBookId);
      } else {
        brandBook = await brandBookService.getBrandBookByDomain(context.orgId, params.domain);
      }

      if (!brandBook) {
        return {
          success: false,
          found: false,
          message: 'No BrandBook found. Create one first.'
        };
      }

      // Extract ad copy context
      const adContext = brandBookService.getAdCopyContext(brandBook);

      return {
        success: true,
        adCopyContext: {
          businessName: adContext.businessName,
          usp: adContext.usp,
          tagline: adContext.tagline,
          offerings: adContext.offerings,
          voice: {
            addressForm: adContext.addressForm,
            tone: adContext.tone,
            formality: adContext.formality
          },
          keyMessages: adContext.keyMessages,
          doStatements: adContext.doStatements,
          dontStatements: adContext.dontStatements,
          copyGuidelines: brandBook.guidelines?.copyGuidelines?.ads
        },
        readyForAdCopy: true
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to get ad copy context: ${error.message}`
      };
    }
  }
};

// ============================================================================
// REGISTER ALL BRAND BOOK TOOLS
// ============================================================================

export function registerBrandBookTools(): void {
  const server = getMCPServer();
  
  server.registerTool(getBrandBookTool, {
    category: 'brand-book',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(createBrandBookTool, {
    category: 'brand-book',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(listBrandBooksTool, {
    category: 'brand-book',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(getImageContextTool, {
    category: 'brand-book',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(getAdCopyContextTool, {
    category: 'brand-book',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  console.log('✅ Registered 5 BrandBook MCP tools');
}

